import React, { useState, useEffect, useRef, useCallback } from "react";
import CommonDataService from "../../services/common";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import LinearProgress from "@mui/material/LinearProgress";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import CollectionsIcon from "@mui/icons-material/Collections";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Typography from "@mui/material/Typography";

import ImageListItemBar from "@mui/material/ImageListItemBar";
import IconButton from "@mui/material/IconButton";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { parseNumber } from "../../helpers/common";

function srcset(image, width, height, rows = 1, cols = 1) {
	return {
		src: `${image}`,
		srcSet: `${image}`,
	};
}

const Alert = React.forwardRef(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const UploadImages = (props) => {
	const [selectedFiles, setSelectedFiles] = useState(undefined);
	const [imagePreviews, setImagePreviews] = useState([]);
	const [progressInfos, setProgressInfos] = useState({ val: [] });
	const [message, setMessage] = useState([]);
	const [imageInfos, setImageInfos] = useState([]);
	const [deleteIds, setDeleteIds] = useState([]);
	const progressInfosRef = useRef(null);
	const [openSnack, setOpenSnack] = React.useState(false);

	const setDelItems = useCallback(
		(event) => {
			props.setDelete(deleteIds);
		},
		[props, deleteIds]
	);

	useEffect(() => {
		setDelItems(deleteIds);
	}, [setDelItems, deleteIds]);

	useEffect(() => {
		CommonDataService.getGalleryItem(
			parseNumber(props.id),
			deleteIds.join(",")
		).then((response) => {
			setImageInfos(response.data);
		});
	}, [props.id, deleteIds]);

	useEffect(() => {
		setMessage([]);
	}, []);

	const selectFiles = (event) => {
		let images = [];

		for (let i = 0; i < event.target.files.length; i++) {
			images.push(URL.createObjectURL(event.target.files[i]));
		}

		setSelectedFiles(event.target.files);
		setImagePreviews(images);
		setProgressInfos({ val: [] });
		setMessage([]);
	};

	const upload = (idx, file) => {
		let _progressInfos = [...progressInfosRef.current.val];
		return CommonDataService.uploadGalleryItem(
			file,
			(event) => {
				_progressInfos[idx].percentage = Math.round(
					(100 * event.loaded) / event.total
				);
				setProgressInfos({ val: _progressInfos });
			},
			props.token,
			parseNumber(props.id),
			deleteIds
		)
			.then(() => {
				setMessage((prevMessage) => [
					...prevMessage,
					"Uploaded the image successfully: " + file.name,
				]);
				setOpenSnack(true);
			})
			.catch(() => {
				_progressInfos[idx].percentage = 0;
				setProgressInfos({ val: _progressInfos });

				setMessage((prevMessage) => [
					...prevMessage,
					"Could not upload the image: " + file.name,
				]);

				setOpenSnack(true);
			});
	};

	const uploadImages = (event) => {
		event.preventDefault();

		const files = Array.from(selectedFiles);

		let _progressInfos = files.map((file) => ({
			percentage: 0,
			fileName: file.name,
		}));

		progressInfosRef.current = {
			val: _progressInfos,
		};

		const uploadPromises = files.map((file, i) => upload(i, file));

		Promise.all(uploadPromises)
			.then(() =>
				CommonDataService.getGalleryItem(
					parseNumber(props.id),
					deleteIds.join(",")
				)
			)
			.then((files) => {
				setImageInfos(files.data);
			});

		setMessage([]);
	};

	const showImagePreviews = () => {
		if (imagePreviews !== null) {
			if (imagePreviews.length > 0) {
				return (
					<div>
						{imagePreviews.map((img, i) => {
							return (
								<img
									height={50}
									className="preview"
									src={img}
									alt={"image-" + i}
									key={i}
								/>
							);
						})}
					</div>
				);
			}
		}
	};

	const removeImageItem = (id) => {
		setImageInfos((current) => current.filter((item) => item.id !== id));
		setDeleteIds((deleteIds) => [...deleteIds, id]);
	};

	const handleClose = (event, reason) => {
		if (reason === "clickaway") {
			return;
		}

		setOpenSnack(false);
	};

	const showImageInfos = () => {
		if (imageInfos !== null) {
			if (imageInfos.length > 0) {
				const cols = 1;
				const rows = 1;

				return (
					<ImageList
						sx={{
							width: "100%",
							height: 450,
							// Promote the list into its own layer in Chrome. This costs memory, but helps keeping high FPS.
							transform: "translateZ(0)",
						}}
						rowHeight={100}
						gap={0}
						cols={4}
					>
						{imageInfos.map((item, i) => (
							<ImageListItem key={i} cols={cols} rows={rows}>
								<img
									{...srcset(item.url, 150, 150, rows, cols)}
									loading="lazy"
									alt=""
								/>
								<ImageListItemBar
									sx={{
										background:
											"linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, " +
											"rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
									}}
									position="top"
									actionIcon={
										<IconButton
											sx={{ color: "white" }}
											aria-label={`star halo empat`}
										>
											<DeleteForeverIcon />
										</IconButton>
									}
									actionPosition="right"
									onClick={(e) => removeImageItem(item.id)}
								/>
							</ImageListItem>
						))}
					</ImageList>
				);
			}
		}
	};

	return (
		<Grid item xs={12} sm={12}>
			<Button
				startIcon={<CollectionsIcon />}
				variant="outlined"
				component="label"
			>
				Select Item Images
				<input
					hidden
					onChange={selectFiles}
					accept="image/*"
					multiple
					type="file"
				/>
			</Button>

			<Button
				startIcon={<FileUploadIcon />}
				sx={{ ml: 1 }}
				variant="contained"
				disabled={!selectedFiles}
				onClick={(e) => uploadImages(e)}
			>
				Upload
			</Button>

			<Snackbar open={openSnack} autoHideDuration={6000} onClose={handleClose}>
				<Alert onClose={handleClose} severity="info" sx={{ width: "100%" }}>
					{message !== null && (
						<ul>
							{message.map((item, i) => {
								return <li key={i}>{item}</li>;
							})}
						</ul>
					)}
				</Alert>
			</Snackbar>

			{progressInfos &&
				progressInfos.val.length > 0 &&
				progressInfos.val.map((progressInfo, index) => (
					<div>
						<Typography variant="overline" display="block" gutterBottom>
							{progressInfo.fileName}
						</Typography>
						<LinearProgress
							variant="determinate"
							value={progressInfo.percentage}
						/>
						{progressInfo.percentage}%
					</div>
				))}

			{showImagePreviews()}

			{showImageInfos()}
		</Grid>
	);
};

export default UploadImages;
